<template>
    <component
        :is="cardComponent"
        v-if="cardComponent && !data.error"
        :result="data.result"
        :tool-name="data.tool_name"
        @submit="(payload: { message: string }) => emit('submit', payload)" />
    <details v-else class="max-w-[80%] text-xs text-slate-500">
        <summary class="cursor-pointer hover:text-slate-700">
            <span class="rounded-full bg-emerald-50 px-2 py-0.5 font-medium text-emerald-800 ring-1 ring-emerald-200">{{ data.tool_name }}</span>
            <span v-if="data.error" class="ml-2 text-rose-600">errored</span>
            <span v-else class="ml-2">returned</span>
        </summary>
        <pre class="mt-1 max-h-64 overflow-auto rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-700">{{ formatted }}</pre>
    </details>
</template>

<script setup lang="ts">
import { computed } from "vue";

import { getToolUiComponent } from "./tool_ui_registry";
import type { ToolResultData } from "./types";

const props = defineProps<{ data: ToolResultData }>();

const emit = defineEmits<{
    // Forwarded from cards that synthesize a follow-up user message (e.g.
    // RolePlaySetupCard's "Start" button). The parent dispatches it as a
    // real user turn so the agent can react.
    (e: "submit", payload: { message: string }): void;
}>();

// Resolve the registered card for this tool's ui_kind. On error we always
// fall back to the debug view so the user (and dev) can see what failed.
const cardComponent = computed(() => getToolUiComponent(props.data.ui_kind));

const formatted = computed(() => {
    if (props.data.error) {
        return props.data.error;
    }
    try {
        return JSON.stringify(props.data.result, null, 2);
    } catch {
        return String(props.data.result);
    }
});
</script>
